import type { ButtonHTMLAttributes, ReactElement, ReactNode } from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';

import { classNames } from '@m1work/shared-utils';

import { Text } from '../Text/Text';

import styles from './IconWithBadge.module.scss';

export interface IconWithBadgeProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    icon: ReactNode;
    /** Text under the icon; shown from `md` up, always the accessible name. */
    label: string;
    /** Number in the badge; no badge for 0 or none. */
    count?: number;
    /** Above this, the badge shows `N+`. */
    maxCountValue?: number;
    /** Render the child (the app's empty `<Link href>`) instead of a `<button>`; icon and label go inside it. @default false */
    asChild?: boolean;
}

/**
 * A navbar item: an icon with a counter badge and a label under it. A button by default; a link with `asChild`.
 *
 * @example
 * <IconWithBadge icon={<CartIcon />} label={t('navbar.cart')} count={count} maxCountValue={99} asChild>
 *     <Link href="/cart" />
 * </IconWithBadge>
 */
export function IconWithBadge({
    icon,
    label,
    count,
    maxCountValue,
    asChild = false,
    className,
    children,
    ...props
}: IconWithBadgeProps): ReactElement {
    const Comp = asChild ? Slot : 'button';

    return (
        <Comp
            type={asChild ? undefined : 'button'}
            className={classNames(styles['icon-with-badge'], className)}
            {...props}
        >
            <div className={styles['icon-wrapper']}>
                <span className={styles.icon}>{icon}</span>
                {count !== undefined && count > 0 && (
                    <span className={styles.badge}>
                        {maxCountValue !== undefined && count > maxCountValue ? `${maxCountValue}+` : count}
                    </span>
                )}
            </div>
            <Text size="sm" align="center" className={styles.label}>
                {label}
            </Text>
            <Slottable>{children}</Slottable>
        </Comp>
    );
}
